import { GetWSMgr } from "@/Utils/StateLib";
import { QURLGenerator } from "@coreapp/CoreApp/QURLGenerator";
import { QTestLinkTokenRFC } from "@coreapp/CoreApp/QTestLinkTokenRFC";
import { Globals } from "@coreapp/CoreApp/Globals";
import { CRC32 } from "@coreapp/utils/CRC32";
import { appstr } from "@coreapp/utils/appstr";

export const WebLinkProps = {
  public: {
    label: "publiclink",
    description: "Anyone with this link can access OrgChart",
    icon: "earth-americas"
  },
  private: {
    label: "privatelink",
    description: "Only existing OrgChart users can access this link",
    icon: "lock-open"
  },
  restricted: {
    label: "restrictedlink",
    description:
      "Anyone with access to the web page on which you embed this link can access OrgChart",
    icon: "lock"
  }
};

export const linkClass = "ocn-btn--link--brand ocn-field-text--medium";

export const selectWithLabelStyle = { direction: "column", width: "50%" };

export const selectWithLink = {
  direction: "row",
  gap: "20px"
};

export const sectionContainer = { direction: "column", gap: "8px" };

export const sectionTitles = {
  linkuser: "Link User Configuration",
  launchconfig: "Launch Configuration",
  presentation: "Presentation",
  startingpoint: "startpoint",
  params: "Additional parameters",
  link: "Link"
};

export const launchModes = ["Chart", "Directory"];

const GetPrivateLinkStart = config => {
  let start = "/?";
  if (config.saml) {
    const entityID = config.entityID;
    start = "/saml/sso_endpoint?entityID=";
    if (!entityID) {
      return false;
    }
    start += entityID + "&";
  }
  return start;
};

const GetLinkDL = (manager, org, linkType, addParam) => {
  let resultURL = "";
  const topboxID = manager.startPoint.topBoxID;
  const launchChart = manager.launchConfig.chartID;
  const chartType = manager.launchConfig.chartType;
  const uiProfile = manager.uiProfile;
  const directory = manager.launchConfig.directory;
  const fileName = manager.launchConfig.fileName;
  const userName = manager.linkUserConfig.linkUser;
  const mode = manager.launchConfig.launchMode;
  let parameters = manager.parameters;
  const levels = manager.startPoint.levels;
  const view = manager.startPoint.view;

  if (linkType === "public" || linkType === "restricted") {
    if (!userName) return false;
    resultURL += addParam(resultURL, userName, "user");
    resultURL += addParam(resultURL, org, "org");
  }

  resultURL += addParam(resultURL, mode.toLowerCase(), "mode");

  // TODO: add handling for planning
  if (chartType === "Master Chart" || chartType === 0) {
    if (!launchChart) {
      const wsmgr = GetWSMgr();
      const chartID = wsmgr.GetOrgInfo().defaultchart;
      resultURL += addParam(resultURL, chartID, "chartID");
    } else {
      resultURL += addParam(resultURL, launchChart, "chartID");
    }

    if (levels) resultURL += addParam(resultURL, levels.toString(), "levels");
    if (view) resultURL += addParam(resultURL, view.toString(), "view");
  } else {
    if (!fileName || fileName === "") return false;
    else resultURL += addParam(resultURL, fileName, "file");

    if (!directory || directory === "") return false;
    else resultURL += addParam(resultURL, directory, "directory");
  }

  if (uiProfile) resultURL += addParam(resultURL, uiProfile, "launchprofile");
  if (topboxID) resultURL += addParam(resultURL, topboxID, "topboxid");

  if (parameters) {
    if (parameters.charAt(0) === "&") {
      resultURL += parameters;
    } else {
      parameters = "&" + parameters;
      resultURL += parameters;
    }
  }

  return resultURL;
};

export const GenerateLink = (urlParamsMgr, setUrlParamsMgr, linkType, org, setError) => {
  if (!urlParamsMgr.linkUserConfig.linkUser && linkType !== "private") {
    setError(appstr("LinkUserNotSelectedError"));
    return;
  }

  let wsMgr = GetWSMgr();
  const eventTracker = wsMgr.GetUsageTracker();
  const origin = window.location.origin;
  const addParam = new QURLGenerator().AddParam;

  let start = "/serverside/api/secureopenchart?";
  if (linkType === "private") {
    start = GetPrivateLinkStart(urlParamsMgr.linkUserConfig, org);
  }

  let theUrl = GetLinkDL(urlParamsMgr, org, linkType, addParam);
  let crc = CRC32.str(theUrl);
  theUrl += addParam(theUrl, crc, "crc");
  let dl = "dl=" + window.btoa(theUrl).replace(/=/g, "");
  var fullStr = origin + start + dl;

  if (fullStr) setUrlParamsMgr(prevState => ({ ...prevState, link: fullStr }));
  eventTracker.ReportEvent("chart_mode.share_link.created", { link_type: linkType });
};

const RequestTestLinkToken = async (linkUser, org, dl) => {
  const wsMgr = GetWSMgr();
  const rfc = new QTestLinkTokenRFC();
  rfc.InitRFC(wsMgr);
  rfc.SetParams(org, wsMgr.GetUserName(), linkUser, dl);
  const responsePromise = rfc.GetPromise();
  Globals._rfcMgr?.QueueRFC(rfc);
  try {
    const result = JSON.parse(await responsePromise);
    return result?.success && result.token ? result.token : null;
  } catch {
    return null;
  }
};

export const TestLink = async (link, linkType, linkUser, org, setError) => {
  let parsed;
  try {
    parsed = new URL(link);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return;
  } catch {
    return;
  }

  if (linkType === "restricted") {
    const token = await RequestTestLinkToken(linkUser, org, parsed.searchParams.get("dl"));
    if (!token) {
      setError?.(appstr("TestLinkTokenError"));
      return;
    }
    const previewUrl = parsed.origin + parsed.pathname + "?testtoken=" + encodeURIComponent(token);
    window.open(previewUrl, "_blank", "noopener,noreferrer");
    return;
  }

  window.open(link, "_blank", "noopener,noreferrer");
};
